import { IonContent, IonPage, IonText, IonCheckbox, IonNavLink } from '@ionic/react'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { ResProductDetail, VALIDATE_STATUS } from '../../../../common'
import { CustomSpacer, FooterButtons, StyleIonHeader, TextStyle } from '../../../../components'
import { DivideLine } from '../../../../components/atoms/divide'
import { LoadingModal } from './loadingModal'

type Props = {
  productData: ResProductDetail
  orderType: 'BUY' | 'SELL'
  orderCost: number
  orderCount: number
  onDismiss: () => void
}

export const TradeAgreeModal: React.FC<Props> = observer(
  ({ productData, orderType, orderCost, orderCount, onDismiss }) => {
    const [validateStatus, setValidateStatus] = useState<VALIDATE_STATUS>(VALIDATE_STATUS.CHECK)
    const [checkedTerms, setCheckedTerms] = useState({ terms1: false, terms2: false, terms3: false, terms4: false })

    const onCheckChange = (event: { target: { name: string; checked: boolean } }) => {
      const name = event.target.name
      setCheckedTerms({
        ...checkedTerms,
        [name]: event.target.checked,
      })
    }

    const validate = () => {
      if (
        checkedTerms.terms1 === true &&
        checkedTerms.terms2 === true &&
        checkedTerms.terms3 === true &&
        checkedTerms.terms4 === true
      ) {
        setValidateStatus(VALIDATE_STATUS.OKAY)
      } else {
        setValidateStatus(VALIDATE_STATUS.CHECK)
      }
    }

    useEffect(() => {
      validate()
    }, [checkedTerms])

    return (
      <IonPage>
        <StyleIonHeader type={'NAV'} title={'유의사항'} />
        <IonContent>
          <ProductContainer>
            <ProductInfoContainer>
              <img
                src={productData.imageUri}
                alt={productData.name}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null
                  currentTarget.src = '/img/thumbnail.svg'
                }}
              />
              <div className="product-info">
                <IonText className="artist">{productData.artist}</IonText>
                <IonText className="title">{productData.name}</IonText>
              </div>
            </ProductInfoContainer>
          </ProductContainer>
          <SectionContainer>
            <TextStyle type={'b_16'}>마켓 거래시 유의사항</TextStyle>
            <CustomSpacer spacing="16px" />
            <BetweenWrap>
              <div className="content">
                <TextStyle type={'r_13'}>
                  매각 대금을 지급받음으로써 미술작품 매각 관련 절차가 완료되었음을 확인합니다. (필수)
                </TextStyle>
              </div>
              <IonCheckbox
                className={'check_box'}
                aria-label={'check_box'}
                mode="md"
                name="terms1"
                checked={checkedTerms.terms1}
                onIonChange={onCheckChange}
              ></IonCheckbox>
            </BetweenWrap>
            <DivideWrap>
              <DivideLine />
            </DivideWrap>
            <BetweenWrap>
              <div className="content">
                <TextStyle type={'r_13'}>소득세 신고를 위한 개인정보 제공에 동의합니다. (필수)</TextStyle>
              </div>
              <IonCheckbox
                className={'check_box'}
                aria-label={'check_box'}
                mode="md"
                name="terms2"
                checked={checkedTerms.terms2}
                onIonChange={onCheckChange}
              ></IonCheckbox>
            </BetweenWrap>
            <DivideWrap>
              <DivideLine />
            </DivideWrap>
            <BetweenWrap>
              <div className="content">
                <TextStyle type={'r_13'}>
                  화면상의 거래버튼을 반복적으로 누를 경우 주문이 복수로 입력될 가능성이 있으므로 주의하시기 바랍니다.
                </TextStyle>
              </div>
              <IonCheckbox
                className={'check_box'}
                aria-label={'check_box'}
                mode="md"
                name="terms3"
                checked={checkedTerms.terms3}
                onIonChange={onCheckChange}
              ></IonCheckbox>
            </BetweenWrap>
            <DivideWrap>
              <DivideLine />
            </DivideWrap>
            <BetweenWrap>
              <div className="content">
                <TextStyle type={'b_13'}>
                  본인은 미술품 [{productData.name}]의 소유권을 매도인으로부터 양도받고, 매도인과 (회사명)000이
                  체결하였던 미술품 관리 및 처분에 대한 계약을 승계하는데 동의하고 주문을 하겠습니까?
                </TextStyle>
              </div>
              <IonCheckbox
                className={'check_box'}
                aria-label={'check_box'}
                mode="md"
                name="terms4"
                checked={checkedTerms.terms4}
                onIonChange={onCheckChange}
              ></IonCheckbox>
            </BetweenWrap>
          </SectionContainer>
        </IonContent>
        {validateStatus === VALIDATE_STATUS.OKAY && (
          <IonNavLink
            routerDirection="forward"
            component={() => (
              <LoadingModal
                type={'TRADE'}
                onDismiss={onDismiss}
                tradeData={{
                  productData: productData,
                  cost: orderCost, //cost,
                  count: orderCount, //count,
                  orderType: orderType,
                }}
              />
            )}
          >
            <FooterButtons
              button={{
                color: `primary`,
                text: `구매하기`,
              }}
            />
          </IonNavLink>
        )}
        {validateStatus !== VALIDATE_STATUS.OKAY && (
          <FooterButtons
            button={{
              color: 'primary',
              text: '유의사항들을 확인해주세요',
              disabled: true,
            }}
          />
        )}
      </IonPage>
    )
  },
)

const ProductContainer = styled.div`
  padding: 24px 20px;
`

const ProductInfoContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 0px;
  img {
    border-radius: 7px;
    min-width: 80px;
    width: 100%;
    aspect-ratio: 1/1;
    object-fit: cover;
  }
  .product-info {
    grid-area: 1 / 1 / 2 / 5;
    display: flex;
    flex-direction: column;
    font-size: 15px;
    .artist {
      white-space: nowrap;
      color: var(--ion-color-primary_light);
      font-size: 13px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .title {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      word-wrap: break-word;
      font-size: 16px;
      font-weight: 700;
    }
  }
`

const SectionContainer = styled.div`
  padding: 24px 20px;
`

const BetweenWrap = styled.div`
  display: flex;
  justify-content: space-between;
  .content {
    width: 90%;
    .terms_link {
      margin-top: 5px;
      font-size: 13px;
      color: var(--ion-color-secondary_main);
      text-decoration: underline;
      text-decoration-color: var(--ion-color-secondary_main);
    }
  }
  .check_box {
    width: 20px;
    height: 20px;
    margin: 5px 8px 0 0;
    --border-width: 2px;
    --border-color-checked: #000000;
    --checkbox-background-checked: #000000;
    --checkmark-width: 3px;
  }
`

const DivideWrap = styled.div`
  margin: 16px 0;
`
